import { windowEventsManager } from '@folklore/hooks';
import { useEffect, useState } from 'react';

import { isInInputElement } from '../lib/isInElement';

import { useHasModals } from '../contexts/PopupsContext';

function useScrollEnter({
    leave: leaveNow = false,
    disabled = false,
    leaveDisabled = false,
    enterThreshold = 20,
    leaveThreshold = null,
    onEnter = null,
    onLeave = null,
    disableNavigation = false,
} = {}) {
    const hasModals = useHasModals();
    const [entered, setEntered] = useState(false);

    function enter() {
        if (entered) {
            return;
        }
        setEntered(true);
        if (onEnter !== null) {
            onEnter(disableNavigation);
        }
    }

    function leave() {
        if (!entered) {
            return;
        }
        setEntered(false);
        if (onLeave !== null) {
            onLeave(disableNavigation);
        }
    }

    useEffect(() => {
        if (disabled) {
            return () => {};
        }
        let lastScrollY = 0;

        function forceLeave() {
            const focusedElement = document.activeElement || null;
            const interactiveHasFocus = focusedElement !== null && isInInputElement(focusedElement);
            const finalLeaveDisabled = interactiveHasFocus || hasModals || leaveDisabled;
            if (leaveNow && !finalLeaveDisabled && onLeave !== null) {
                leave();
                return true;
            }
            return false;
        }

        function onScroll() {
            if (forceLeave()) {
                return;
            }

            const currentScrollY = window.scrollY;
            const direction = currentScrollY < lastScrollY ? -1 : 1;
            lastScrollY = currentScrollY;
            const focusedElement = document.activeElement || null;
            const interactiveHasFocus = focusedElement !== null && isInInputElement(focusedElement);
            const finalLeaveDisabled = interactiveHasFocus || hasModals || leaveDisabled;

            if (
                leaveThreshold !== null &&
                currentScrollY > leaveThreshold &&
                !finalLeaveDisabled &&
                onLeave !== null
            ) {
                leave();
            } else if (currentScrollY > enterThreshold && onEnter !== null && !entered) {
                enter();
            } else if (
                currentScrollY <= enterThreshold &&
                !finalLeaveDisabled &&
                direction === -1 &&
                onLeave !== null
            ) {
                leave();
            }
        }
        windowEventsManager.subscribe('scroll', onScroll);
        onScroll();

        return () => {
            windowEventsManager.unsubscribe('scroll', onScroll);
        };
    }, [
        disabled,
        leaveNow,
        enter,
        leave,
        enterThreshold,
        leaveThreshold,
        disableNavigation,
        hasModals,
    ]);
}

export default useScrollEnter;
